/**
 * Reading a staple food (food staples plan 4c, #5625, Requirement C5).
 *
 * A food is copied into the user's own database the first time they use it, and never refreshed —
 * so before this, a correction to a curated staple never reached anybody who had already eaten it.
 * Now that every device holds the curated staples, that copy is the better one, and it is the one
 * shown; the part of their own copy that is genuinely theirs is carried across.
 *
 * The rules are here rather than in `FoodDbService` so they can be unit-tested: that is an
 * `@Injectable` service and the client's unit tests are plain node tests.
 */

import { Food } from '@cf-shared/data-model/food';
import { deepClone } from '@cf-shared/deep-helpers';

/** the id prefix every staple food has; mirrored from `staple-db-snapshot.ts` */
const STAPLE_ID_PREFIX = 'food/$STP-';

/**
 * Is this a curated staple's id?
 *
 * Asked BEFORE the device's copy is looked up, so reading any other food does not wait on the staple
 * snapshot at all — `getFoodByIdAsync` runs on every ingredient of every meal that is opened.
 */
export function isStapleId(id: string): boolean {
  return typeof id === 'string' && id.startsWith(STAPLE_ID_PREFIX);
}

/**
 * The curated staple to show, carrying whatever of the person's own copy is really theirs.
 *
 * WHAT IS TAKEN FROM WHICH. Every value — the name, the nutrients, the measures, the description —
 * comes from the CURATED document, because that is the whole point: a correction reaches everybody,
 * including somebody who ate the food before it was corrected. The `local` block comes from THEIR
 * copy, because that is the only part of a food that is about them: the like mark and when they last
 * used it. The ranking weights a food by that date and the Favorites and Logged-Before filters
 * decide by it, so dropping it would lose a food out of their own filtered lists.
 *
 * NOTHING IS WRITTEN BACK. The caller's `cacheFood` option is deliberately ignored for a staple: a
 * write-back would bump a revision in the user's own database — and push it to the server and to
 * their other devices — on every staple read, to store a copy that is already staler than the one
 * the device syncs.
 *
 * THE TRADE-OFF THIS ACCEPTS. A logged meal keeps its own stored nutrients, so past meals do not
 * change on their own. But an ingredient stores only a food id with a cached name and calorie count,
 * and is recomputed from this read when a meal is opened or re-weighed — so opening a past meal
 * after a staple correction CAN change its numbers. That is intended: the curated value is the
 * better one. `design-docs/food-staples.md` records it.
 *
 * DEEP-CLONED, and a shallow copy is genuinely not enough. `curated` is the object the device's
 * snapshot holds and hands to every other caller — the search headers point at it, and the
 * pre-analysed word map keeps it by identity for the whole session. A spread would share its
 * `measures`, `nutrients` and `staple` objects, and two callers write through them: the reader
 * runs `normalizeFoodFormat`, which rewrites measure ELEMENTS in place, and `cacheFoodAsync`
 * assigns each key onto the user's own stored document by reference, after which any later edit
 * of theirs would reach back into the snapshot. Both are silent and neither would point here.
 */
export function stapleWithUserHistory(curated: Food, theirCopy: Food | undefined): Food {
  const merged: Food = deepClone(curated);
  // their own history, and only that; an absent block is left absent rather than set to undefined,
  // so the merged document has the same shape as one nobody has eaten. Cloned for the same reason
  // as the rest: the answer travels on to `cacheFoodAsync`, which assigns by reference.
  if (theirCopy?.local) {
    merged.local = deepClone(theirCopy.local);
  }
  return merged;
}

/**
 * The staple to answer a read with, or null to fall through to the ordinary read.
 *
 * The DECISION, not just the merge, so it can be tested: that a staple is answered from the
 * device's copy IN PREFERENCE TO the person's own cached copy is the whole point of Requirement
 * C5, and it is a property of which candidate wins rather than of how they are combined. Moving
 * that choice back into the service would leave it untested — the service is an Angular class and
 * the client's unit tests are plain node tests.
 *
 * @param id        the food being read
 * @param curated   the device's copy of that staple, or null when there is none to offer
 * @param theirCopy the person's own cached copy, when they have used the food before
 */
export function preferredStapleRead(id: string, curated: Food | null | undefined, theirCopy: Food | undefined): Food | null {
  // not a staple, or this device has not got it yet: the ordinary read is the right answer
  if (!isStapleId(id) || !curated) {
    return null;
  }
  return stapleWithUserHistory(curated, theirCopy);
}
